/**
 * Pure helpers for the sales view: who owns which pilot deal and what the
 * owner should do next. Kept out of the components so the rules can be unit
 * tested.
 */

import { addPlaybookAction, addSharedChecks, createTodoList } from "./todos";

/** What the deal owner does next, keyed by the pilot's enterprise playbook step. */
const NEXT_SALES_ACTION = {
  pilot_users_identified: "Lock the kickoff date with the client.",
  pilot_kickoff_scheduled:
    "Confirm the kickoff happened and the pilot users can log in.",
  week_one_meeting_complete:
    "Check in on week-one feedback before the week-two call.",
  week_two_meeting_completed:
    "Line up the week-three rollout meeting and who signs off.",
  week_three_rollout_meeting_completed: "Book the internal pilot review.",
  internal_pilot_review_completed: "Deliver the proposal package.",
};

/**
 * Everyone who owns a pilot deal, running or past, sorted by name.
 *
 * @param {object[]} pilots
 * @returns {Array<{id: string, name: string}>}
 */
export function buildOwnerDirectory(pilots = []) {
  const owners = new Map();
  pilots.forEach((pilot) => {
    if (pilot.ownerId && pilot.owner && !owners.has(pilot.ownerId)) {
      owners.set(pilot.ownerId, { id: pilot.ownerId, name: pilot.owner });
    }
  });

  return [...owners.values()].sort((a, b) => a.name.localeCompare(b.name));
}

export function pilotsForOwner(pilots, ownerId) {
  if (!ownerId) {
    return [];
  }

  return pilots.filter((pilot) => pilot.ownerId === ownerId);
}

/**
 * What a deal owner should do next across their running pilots, most urgent
 * first.
 *
 * @param {object[]} pilots the owner's running pilots
 * @param {number} [now] injectable clock, for tests
 */
export function buildSalesTodos(pilots, now = Date.now()) {
  const todos = createTodoList();

  pilots.forEach((pilot) => {
    if (!pilot.fde) {
      todos.add(
        pilot,
        "noFde",
        "high",
        "No FDE assigned. Set the Forward Deployed Engineer in HubSpot.",
      );
    }
    addSharedChecks(todos, pilot, now, { isDealOwner: true });
    addPlaybookAction(todos, pilot, NEXT_SALES_ACTION);
    if (pilot.subStage && !pilot.isInPilotStage) {
      todos.add(
        pilot,
        "stage",
        "normal",
        `HubSpot still shows “${pilot.stage}”. Move the deal to the Pilot stage.`,
      );
    }
    if (!pilot.organizationId) {
      todos.add(
        pilot,
        "unlinked",
        "low",
        "Could not find the client's Addy account from the deal's contacts. Add the pilot users as contacts on the deal.",
      );
    }
  });

  return todos.list();
}
